import { Loader as MapsAPILoader } from "@googlemaps/js-api-loader";
import { TextDisplay } from "./text.js";

import {
  scheduleScript,
} from "./script/index.js";
import { intro1 } from './script/intro.js';

import { Sharawadji } from "./sharawadji/index.js";
import { fakeCaptcha } from "./interactions/fakeCaptcha.js";
import initHUD, { enterFullscreen, setLatLngDisplay, setPovDisplay } from './hud.js';
import { resetGame } from './reset.js';
import { LOCALSTORAGE_POSITION_KEY, START_POSITION, START_POV, MAPS_API_KEY } from './constants.js';
import loadItems from './items/index.js';
import { completed as completedChapters } from './chapters.js';
import initGamepad from './gamepad.js';
import { initTask } from './task.js';
import { initSpeech } from './script/speech.js';
import { localisedSounds } from './audio/localised-sounds.js';
import { FADE_OUT_DELAY_MS, playGatewaySound } from './audio/gateway-sound.js';
import { enableClickToGoCB, enableSFX, persistenceToast } from './script/utils.js';
import { checkForCheckpoints } from './checkpoints.js';
import createSFX from './audio/sfx.js';
import createScore from './audio/score-sounds.js';
import createSoundscape from './audio/soundscapes.js';
import { initJournal } from './journal/index.js';
import { initCustomPanorama } from './custom-panorama/index.js';
import { createDelayEffect } from './items/shrooms.js';
import { LIMBO_LNG_STEP } from './custom-panorama/limbo.js';
import { checkSafari } from './safari.js';
import { button as cruiseControlButton } from './cruise-control.js';
import { initPanoRecovery } from './pano-recovery.js';
import { initKiosk } from "./kiosk.js";
import { initDemo } from "./demo.js";

const container = document.getElementById("container");
const intro = document.getElementById("intro");
const introCTAFromScratch = document.getElementById("intro-cta-from-scratch");
const introCTAContinue = document.getElementById("intro-cta-continue");

const textContainer = document.getElementById("text-display");
const statusContainer = document.getElementById("status-display");
const helpContainer = document.getElementById("help-display");
const fakeCaptchas = Array.from(document.getElementsByClassName("fake-captcha")).map(c => fakeCaptcha(c));
const textDisplay = new TextDisplay(textContainer);

const currentURL = new URL(location.href);
const debug = currentURL.searchParams.get('debug') === 'true';
const dev = currentURL.searchParams.get('dev') === 'true';
const cinema = currentURL.searchParams.get('cinema') === 'true';
const kiosk = currentURL.searchParams.get('kiosk') === 'true';
const demo = currentURL.searchParams.get('demo') === 'true';

const scriptContext = {
  kiosk,
  demo,
  container,
  statusContainer,
  helpContainer,
  fakeCaptchas,
  textDisplay,
};

document.body.classList.toggle('debug', debug);
document.body.classList.toggle('dev', dev);
document.body.classList.toggle('cinema', cinema);
document.body.classList.toggle('kiosk', kiosk);
document.body.classList.toggle('demo', demo);

const initialPosition = JSON.parse(localStorage.getItem(LOCALSTORAGE_POSITION_KEY)) || START_POSITION;
const mapOptions = {
  position: initialPosition,
  pov: START_POV,
  clickToGo: debug ? true : false,
  disableDefaultUI: true,
  showRoadLabels: false,
};

if (initialPosition.lat === 0) {
  // we're in limbo
  delete mapOptions.position;
  mapOptions.pano = `limbo${Math.round(initialPosition.lng * LIMBO_LNG_STEP)}`;
  mapOptions.pov = { heading: 80, pitch: 0, zoom: 1 };
} else if (initialPosition.lat === 1) {
  delete mapOptions.position;
  mapOptions.pano = `exitLimbo${Math.round(initialPosition.lng * LIMBO_LNG_STEP)}`;
  mapOptions.pov = { heading: 80, pitch: 0, zoom: 1 };
}

if (completedChapters.size > 0) {
  introCTAContinue.hidden = false;
} else if (!debug) {
  document.documentElement.style = `--intro-darkness-duration: ${FADE_OUT_DELAY_MS}ms;`;
}

const initialize = async () => {
  if (checkSafari()) {
    return;
  }

  initHUD(scriptContext);

  if (!dev && !debug && !kiosk) {
    enterFullscreen();
  }

  const mapsLoader = new MapsAPILoader({
    apiKey: MAPS_API_KEY,
    version: '3.exp',
    libraries: ['streetView'],
  });

  const { StreetViewPanorama, InfoWindow, StreetViewService } = await mapsLoader.importLibrary('streetView');
  const { event, ControlPosition, Size, LatLng } = await mapsLoader.importLibrary('core');
  const { Map, MapTypeId } = await mapsLoader.importLibrary('maps');
  scriptContext.google = { event, StreetViewPanorama, StreetViewService, InfoWindow, Size, LatLng, Map, MapTypeId };

  container.hidden = false;

  mapOptions.panControl = true,
  mapOptions.panControlOptions = { position: ControlPosition.BLOCK_START_INLINE_END };

  const map = new StreetViewPanorama(container, mapOptions);
  window.map = map;
  window.scriptContext = scriptContext;
  scriptContext.map = map;

  await initCustomPanorama(scriptContext);

  intro.removeEventListener("click", initialize);
  intro.hidden = true;

  scriptContext.audioContext = new AudioContext();

  scriptContext.masterGain = new GainNode(scriptContext.audioContext, { gain: 0 });
  scriptContext.masterGain.connect(scriptContext.audioContext.destination);

  scriptContext.delay = createDelayEffect(scriptContext);
  scriptContext.masterGain.connect(scriptContext.delay).connect(scriptContext.audioContext.destination);

  scriptContext.speechGain = new GainNode(scriptContext.audioContext, { gain: 0.9 });
  scriptContext.speechGain.connect(scriptContext.masterGain);

  scriptContext.score = createScore(scriptContext);
  scriptContext.soundscape = createSoundscape(scriptContext);

  scriptContext.sfx = await createSFX(scriptContext);

  scriptContext.masterGain.gain.setValueAtTime(1, scriptContext.audioContext.currentTime);

  scriptContext.localisedSounds = new Sharawadji(localisedSounds, scriptContext.map, {
    debug,
    compressor: true
  });

  if (!debug && completedChapters.size === 0) {
    initialSequence(scriptContext);
  } else {
    revisitedSequence(scriptContext);
  }

  if (!map.getPosition()) {
    console.info('[main]', 'map does not have position');
    map.setPosition(START_POSITION);
  }

  scriptContext.items = await loadItems(InfoWindow, scriptContext);
  scriptContext.items.forEach((o) => o.update());

  checkForCheckpoints(scriptContext)();

  event.addListener(
    map,
    "position_changed",
    () => {
      checkForCheckpoints(scriptContext)();
      scriptContext.sfx.footsteps();

      const position = { lat: map.getPosition().lat(), lng: map.getPosition().lng() };
      setLatLngDisplay(position);

      localStorage.setItem(LOCALSTORAGE_POSITION_KEY, JSON.stringify(position));
      scriptContext.items.forEach((o) => o.update());
    },
  );

  event.addListener(
    map,
    "pov_changed",
    () => {
      const pov = map.getPov();
      setPovDisplay(pov);

      scriptContext.items.forEach((o) => o.povUpdate?.());
    },
  );

  initTask();
  initSpeech(scriptContext);

  initGamepad(scriptContext);
  setLatLngDisplay(initialPosition);
  setPovDisplay(map.getPov());
  initJournal();
  initPanoRecovery(scriptContext);

  if (kiosk) {
    initKiosk(scriptContext);
  }

  if (demo) {
    initDemo(scriptContext);
  }

  document.body.classList.add('game-on');

};

const initialSequence = (context) => {
  console.info('[main]', 'initial sequence');
  document.documentElement.style = `--intro-darkness-duration: ${FADE_OUT_DELAY_MS}ms;`;

  setTimeout(() => {
    playGatewaySound(context);
  }, 1000);

  setTimeout(() => {
    context.soundscapeGain.gain.setValueAtTime(0, context.audioContext.currentTime);
    context.soundscapeGain.gain.linearRampToValueAtTime(1, context.audioContext.currentTime + 2);
    scheduleScript(intro1, context);
  }, FADE_OUT_DELAY_MS);
}

const revisitedSequence = (context) => {
  console.info('[main]', 'revisited sequence');

  setTimeout(() => {
    enableSFX(context);
    persistenceToast();
  }, 10000);

  enableClickToGoCB(context);
  context.soundscapeGain.gain.setValueAtTime(0, context.audioContext.currentTime);
  context.soundscapeGain.gain.linearRampToValueAtTime(1, context.audioContext.currentTime + 2);
  cruiseControlButton.hidden = false;
};

// let startGamepadPoll;

// const gamepadStarter = () => {
//   if (scriptContext.map) return;

//   startGamepadPoll = requestAnimationFrame(gamepadStarter);

//   const gamepad = navigator.getGamepads()[0];
//   if (!gamepad) {
//     return;
//   }

//   if (gamepad.buttons[1]?.value > 0) {
//     start();
//   }
// };

// gamepadStarter();

const start = () => {
  // cancelAnimationFrame(startGamepadPoll);
  mapOptions.position = START_POSITION;
  mapOptions.pov = START_POV;
  resetGame();
  initialize();
};


introCTAFromScratch.addEventListener('click', start);
introCTAContinue.addEventListener('click', initialize);
